// Who an uploaded row is assigned to. Leadership and admin only: a CSV owner cell
// wins, a blank cell falls back to the default owner, and no default leaves the
// row in the bank. An owner that names nobody listed is a visible problem, never
// a silent drop to the bank.

import type { StrategistOption } from '../api/types';
import { strategistLabel } from './queueSignal.ts';

export const BANK_OWNER_VALUE = '';
export const ASSIGN_ON_UPLOAD_REASON = 'assigned on upload';

// Which way an upload writes, handed to the page by the server as `intake_mode`. An
// approver assigns; a strategist's rows become requests an approver decides on.
export type IntakeMode = 'assign' | 'request';

export interface IntakeOwnerChoice {
  email: string;
  label: string;
}

export interface PlannedOwner {
  // The listed strategist's email, or BANK_OWNER_VALUE for "leave in the bank".
  email: string;
  // Where the owner came from, so the preview can say "from CSV" vs "default".
  source: 'csv' | 'default' | 'bank';
  // Set when the CSV named someone who is not a listed strategist.
  problem: string;
}

export function intakeOwnerChoices(strategists: StrategistOption[]): IntakeOwnerChoice[] {
  const bank: IntakeOwnerChoice = {
    email: BANK_OWNER_VALUE,
    label: 'No default — leave them in the bank',
  };
  const listed = strategists.map(row => ({
    email: row.email,
    label: strategistLabel(row.email, strategists),
  }));
  listed.sort((left, right) => left.label.localeCompare(right.label));
  return [bank, ...listed];
}

// The single-account form always names someone. Leaving it in the bank is a
// CSV-list behaviour, not a net-new request.
export function netNewOwnerChoices(strategists: StrategistOption[]): IntakeOwnerChoice[] {
  return intakeOwnerChoices(strategists).filter(choice => choice.email !== BANK_OWNER_VALUE);
}

export function shouldAssignAfterIntake(
  outcome: string | undefined,
  accountId: string | null | undefined,
): boolean {
  if (outcome === 'excluded') return false;
  return Boolean(accountId);
}

export function shouldClaimAfterIntake(
  _outcome: string | undefined,
  accountId: string | null | undefined,
): boolean {
  // Request mode: an excluded row with a live Account still goes to claim_account,
  // which flags or refuses. Assign mode keeps skipping excluded rows.
  return Boolean(accountId);
}

// Which Account id a request should carry after the door. The door's id is the one it
// resolved -- except on `ambiguous_match`, where it is simply the first of several
// Accounts sharing the domain (often a "<email> - free" playground signup). Passing
// that on would request an arbitrary duplicate with no flag, so the claim resolves by
// domain instead and queues the request flagged with every candidate for the approver.
export function claimAccountIdFromDoor(
  reason: string | null | undefined,
  accountId: string | null | undefined,
): string | undefined {
  if (reason === 'ambiguous_match') return undefined;
  return accountId || undefined;
}

export function listedStrategistEmail(
  value: string,
  strategists: StrategistOption[],
): string | null {
  const needle = value.trim().toLowerCase();
  if (!needle) return null;

  // An exact email always wins.
  const byEmail = strategists.find(row => row.email.toLowerCase() === needle);
  if (byEmail) return byEmail.email;

  // A full display name, when exactly one strategist carries it.
  const byName = strategists.filter(row => (row.name || '').trim().toLowerCase() === needle);
  if (byName.length === 1 && byName[0]) return byName[0].email;

  // A first name ("Eva"), matched against the display name or the email's local part,
  // when it is unambiguous. Two people sharing a first name resolve to nobody.
  const byFirstName = strategists.filter(row => {
    const nameFirst = (row.name || '').trim().toLowerCase().split(/\s+/)[0] || '';
    const emailFirst = (row.email.split('@')[0] || '').toLowerCase().split('.')[0] || '';
    return nameFirst === needle || emailFirst === needle;
  });
  if (byFirstName.length === 1 && byFirstName[0]) return byFirstName[0].email;

  return null;
}

export function plannedRowOwner(args: {
  csvOwner: string;
  defaultOwner: string;
  strategists: StrategistOption[];
}): PlannedOwner {
  const csvOwner = args.csvOwner.trim();

  // A blank cell uses the default owner, which may itself be the bank.
  if (!csvOwner) {
    if (args.defaultOwner) return { email: args.defaultOwner, source: 'default', problem: '' };
    return { email: BANK_OWNER_VALUE, source: 'bank', problem: '' };
  }

  // A named owner must be a listed strategist. Anything else is reported, not guessed.
  const listed = listedStrategistEmail(csvOwner, args.strategists);
  if (listed) return { email: listed, source: 'csv', problem: '' };
  return {
    email: BANK_OWNER_VALUE,
    source: 'csv',
    problem: `"${csvOwner}" is not a listed strategist`,
  };
}
